import type {
  AdminCompany,
  AdminCompanyDeletionPreview,
  AdminCompanySearchResult,
  AdminDashboardMetrics,
  AdminSyncJob,
  AdminSyncJobId,
  AdminUser,
  AuthUser,
  BetaSignupInput,
  BetaSignupResponse,
  BoatResponse,
  CharterEmailKind,
  CharterEmailStatus,
  CompleteOnboardingNewOrgInput,
  CompSetsMarketStatusResponse,
  CreateIntegrationInput,
  CreateInvitationInput,
  DashboardLayout,
  ForgotPasswordInput,
  GetStartedInput,
  IncomeSummary,
  Integration,
  IntegrationWithCredentials,
  Invitation,
  InvitationTokenInfo,
  LoginInput,
  OccupancySeasonSummary,
  Organization,
  OrgMember,
  OwnerListItem,
  OwnerOption,
  OwnerShareResponse,
  OwnerStatus,
  PaginatedBoats,
  PaginatedUsers,
  PriceSnapshotResponse,
  PricingPeriodResponse,
  RegisterInput,
  ResetPasswordInput,
  RevenueGoalResponse,
  SeasonOverview,
  UpdateIntegrationInput,
  UpdateUserInput,
  UpsertOwnerInput,
  UpsertOwnerShareInput,
  UpsertRevenueGoalInput,
  User,
} from '@sea-snag/shared';
import ky, { type KyInstance } from 'ky';

import { getOrganization } from '../stores/org-store';

const API_BASE_URL = (import.meta.env['VITE_API_URL'] as string) || 'http://localhost:3000';

// API response wrapper from TransformInterceptor
interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta: { timestamp: string; requestId: string };
}

function cleanParams(
  params?: Record<string, string | undefined>
): Record<string, string> | undefined {
  if (!params) return undefined;
  const cleaned: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) cleaned[key] = value;
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

// Token storage (access token only - refresh token is in httpOnly cookie)
let accessToken: string | null = null;

export function setAccessToken(token: string) {
  accessToken = token;
  localStorage.setItem('accessToken', token);
}

export function getAccessToken(): string | null {
  if (!accessToken) {
    accessToken = localStorage.getItem('accessToken');
  }
  return accessToken;
}

export function clearTokens() {
  accessToken = null;
  localStorage.removeItem('accessToken');
}

// Token refresh logic
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = ky
    .post(`${API_BASE_URL}/api/auth/refresh`, { credentials: 'include' })
    .json<ApiResponse<{ accessToken: string }>>()
    .then((res) => {
      const token = res.data?.accessToken;
      if (typeof token !== 'string' || token.length === 0) {
        throw new Error('Invalid refresh response: missing access token');
      }
      setAccessToken(token);
      return token;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// Create ky instance with hooks
const api: KyInstance = ky.create({
  prefixUrl: `${API_BASE_URL}/api`,
  credentials: 'include',
  hooks: {
    beforeRequest: [
      (request) => {
        const token = getAccessToken();
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
      (request) => {
        if (
          request.url.includes('/auth/') ||
          request.url.includes('/admin/') ||
          request.url.includes('/public/')
        )
          return;
        if (request.headers.has('x-organization-id')) return;

        const org = getOrganization();
        if (org?.pid) {
          request.headers.set('x-organization-id', org.pid);
        }
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        if (response.status === 401 && !request.url.includes('/auth/refresh')) {
          try {
            await refreshAccessToken();
            const newToken = getAccessToken();
            request.headers.set('Authorization', `Bearer ${newToken}`);
            return ky(request);
          } catch {
            clearTokens();
            window.location.href = '/login';
            throw new Error('Session expired');
          }
        }
        return response;
      },
    ],
  },
  retry: {
    limit: 2,
    methods: ['get', 'put', 'delete'],
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
  },
});

// Type-safe API methods
export const authApi = {
  login: (data: LoginInput) =>
    api
      .post('auth/login', { json: data })
      .json<ApiResponse<{ accessToken: string }>>()
      .then((res) => res.data),

  register: (data: RegisterInput) =>
    api
      .post('auth/register', { json: data })
      .json<
        ApiResponse<{
          accessToken: string;
          organization: { id: string; name: string; slug: string };
        }>
      >()
      .then((res) => res.data),

  refresh: () =>
    api
      .post('auth/refresh')
      .json<ApiResponse<{ accessToken: string }>>()
      .then((res) => res.data),

  logout: () => api.post('auth/logout').json<ApiResponse<{ message: string }>>(),

  me: () =>
    api
      .get('auth/me')
      .json<ApiResponse<AuthUser>>()
      .then((res) => res.data),

  forgotPassword: (data: ForgotPasswordInput) =>
    api
      .post('auth/forgot-password', { json: data })
      .json<ApiResponse<{ message: string }>>()
      .then((res) => res.data),

  resetPassword: (data: ResetPasswordInput) =>
    api
      .post('auth/reset-password', { json: data })
      .json<ApiResponse<{ message: string }>>()
      .then((res) => res.data),

  getStarted: (data: GetStartedInput) =>
    api
      .post('auth/get-started', { json: data })
      .json<ApiResponse<{ message: string }>>()
      .then((res) => res.data),

  validateOnboardingToken: (token: string) =>
    api
      .get('auth/onboarding', { searchParams: { token } })
      .json<ApiResponse<{ email: string; mode: 'new' | 'new-org' }>>()
      .then((res) => res.data),

  completeOnboarding: (data: {
    token: string;
    name: string;
    organizationName: string;
    teamSize: string;
    password: string;
  }) =>
    api
      .post('auth/onboarding', { json: data })
      .json<
        ApiResponse<{
          accessToken: string;
          organization: { id: string; name: string; slug: string };
        }>
      >()
      .then((res) => res.data),

  completeOnboardingNewOrg: (data: CompleteOnboardingNewOrgInput) =>
    api
      .post('auth/onboarding/new-org', { json: data })
      .json<
        ApiResponse<{
          accessToken: string;
          organization: { id: string; name: string; slug: string };
        }>
      >()
      .then((res) => res.data),
};

export const usersApi = {
  list: (params?: { page?: number; limit?: number; search?: string; isActive?: boolean }) =>
    api
      .get('users', {
        searchParams: params as Record<string, string | number | boolean>,
      })
      .json<ApiResponse<PaginatedUsers>>()
      .then((res) => res.data),

  get: (id: string) =>
    api
      .get(`users/${id}`)
      .json<ApiResponse<User>>()
      .then((res) => res.data),

  update: (id: string, data: UpdateUserInput) =>
    api
      .patch(`users/${id}`, { json: data })
      .json<ApiResponse<User>>()
      .then((res) => res.data),

  delete: (id: string) => api.delete(`users/${id}`),
};

export const invitationsApi = {
  create: (orgPid: string, data: CreateInvitationInput) =>
    api
      .post(`organizations/${orgPid}/invitations`, { json: data })
      .json<ApiResponse<Invitation>>()
      .then((res) => res.data),

  list: (orgPid: string) =>
    api
      .get(`organizations/${orgPid}/invitations`)
      .json<ApiResponse<Invitation[]>>()
      .then((res) => res.data),

  revoke: (orgPid: string, invitationId: string) =>
    api.delete(`organizations/${orgPid}/invitations/${invitationId}`),

  getByToken: (token: string) =>
    api
      .get(`invitations/accept/${token}`)
      .json<ApiResponse<InvitationTokenInfo>>()
      .then((res) => res.data),

  linkExistingUser: (token: string) =>
    api
      .post(`invitations/accept/${token}/link`)
      .json<ApiResponse<{ organizationPid: string; role: string }>>()
      .then((res) => res.data),
};

export const membersApi = {
  list: (orgPid: string) =>
    api
      .get(`organizations/${orgPid}/members`)
      .json<ApiResponse<OrgMember[]>>()
      .then((res) => res.data),
  /**
   * Change one member's role. The server decides whether it is allowed — see
   * `canAssignRole` — and its message is what the user should be shown.
   */
  updateRole: (orgPid: string, userPid: string, role: string) =>
    api
      .patch(`organizations/${orgPid}/members/${userPid}`, { json: { role } })
      .json<ApiResponse<OrgMember>>()
      .then((res) => res.data),
};

export const integrationsApi = {
  create: (orgPid: string, data: CreateIntegrationInput) =>
    api
      .post(`organizations/${orgPid}/integrations`, { json: data })
      .json<ApiResponse<Integration>>()
      .then((res) => res.data),

  list: (orgPid: string) =>
    api
      .get(`organizations/${orgPid}/integrations`)
      .json<ApiResponse<Integration[]>>()
      .then((res) => res.data),

  get: (orgPid: string, integrationId: string) =>
    api
      .get(`organizations/${orgPid}/integrations/${integrationId}`)
      .json<ApiResponse<IntegrationWithCredentials>>()
      .then((res) => res.data),

  update: (orgPid: string, integrationId: string, data: UpdateIntegrationInput) =>
    api
      .patch(`organizations/${orgPid}/integrations/${integrationId}`, { json: data })
      .json<ApiResponse<Integration>>()
      .then((res) => res.data),

  remove: (orgPid: string, integrationId: string) =>
    api.delete(`organizations/${orgPid}/integrations/${integrationId}`),

  validate: (orgPid: string, integrationId: string) =>
    api
      .post(`organizations/${orgPid}/integrations/${integrationId}/validate`)
      .json<ApiResponse<{ valid: boolean; status: string }>>()
      .then((res) => res.data),

  sync: (orgPid: string, integrationId: string) =>
    api
      .post(`organizations/${orgPid}/integrations/${integrationId}/sync`)
      // alreadyQueued: a sync was already waiting or running, so none was started.
      .json<ApiResponse<{ jobId: string; alreadyQueued: boolean }>>()
      .then((res) => res.data),
};

export const boatsApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    kind?: string;
    isActive?: string;
    sortBy?: string;
    sortOrder?: string;
  }) =>
    api
      .get('boats', {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedBoats>>()
      .then((res) => res.data),

  get: (id: string) =>
    api
      .get(`boats/${id}`)
      .json<ApiResponse<BoatResponse>>()
      .then((res) => res.data),

  pricing: (boatId: string) =>
    api
      .get(`boats/${boatId}/pricing`)
      .json<ApiResponse<PaginatedResponse<PricingPeriodResponse>>>()
      .then((res) => res.data),

  priceHistory: (boatId: string) =>
    api
      .get(`boats/${boatId}/price-history`)
      .json<ApiResponse<PaginatedResponse<PriceSnapshotResponse>>>()
      .then((res) => res.data),
};

export const organizationsApi = {
  my: () =>
    api
      .get('organizations/my')
      .json<ApiResponse<Organization[]>>()
      .then((res) => res.data),
};

// ── Pricing Intelligence Types ──

export interface PaginatedResponse<T> {
  data: T[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface MarketBoat {
  id: string;
  externalId: string;
  name: string;
  kind: string;
  model: string | null;
  shipyard: string | null;
  year: number | null;
  lengthM: string | null;
  beamM: string | null;
  draughtM: string | null;
  cabins: number | null;
  berths: number | null;
  wc: number | null;
  maxPeople: number | null;
  charterName: string | null;
  homeBase: string | null;
  companyId: number | null;
  defaultCurrency: string | null;
  isActive: boolean;
  isTracked: boolean;
  lastSeenAt: string;
  createdAt: string;
  priceMin: string | null;
  priceMax: string | null;
  priceCurrency: string | null;
  equipment: EquipmentItem[];
  /** Equipment index, 100 = the median boat of the same hull type. Null when unscorable. */
  equipmentScore: EquipmentScore | null;
  imageCount: number;
}

export interface MarketStats {
  trackedBoats: number;
  trackedOperators: number;
  totalBoats: number;
  lastSyncAt: string | null;
}

export interface MarketPricingPeriod {
  id: string;
  dateFrom: string;
  dateTo: string;
  product: string;
  price: string;
  currency: string;
  listPrice: string | null;
  discountPct: string | null;
  source: string;
}

export interface MarketPriceSnapshot {
  id: string;
  dateFrom: string;
  dateTo: string;
  product: string;
  price: string;
  currency: string;
  listPrice: string | null;
  discountPct: string | null;
  fetchedAt: string;
  source: string;
}

export interface CompSet {
  id: string;
  name: string;
  description: string | null;
  referenceBoats: Array<{ id: string; name: string }>;
  filterKind: string | null;
  filterSearch: string | null;
  filterMinLength: string | null;
  filterMaxLength: string | null;
  filterHomeBases: string[];
  filterWorldRegionPids: string[];
  filterCountryPids: string[];
  filterSailingAreaPids: string[];
  filterBasePids: string[];
  filterCharterNames: string[];
  filterModels: string[];
  filterEquipmentIds: number[];
  filterMinCabins: number | null;
  filterMaxCabins: number | null;
  filterMinYear: number | null;
  filterMaxYear: number | null;
  filterMinBerths: number | null;
  filterMaxBerths: number | null;
  filterMinWc: number | null;
  filterMinPeople: number | null;
  filterMinBeam: string | null;
  filterMaxBeam: string | null;
  filterMinDraught: string | null;
  filterMaxDraught: string | null;
  excludedMarketBoatPids: string[];
  smartSyncEnabled: boolean;
  status: string;
  marketBoatCount: number;
  lastSyncAt: string | null;
  lastSyncAddedCount: number | null;
  lastSyncRemovedCount: number | null;
  createdAt: string;
}

export interface CompSetBoat {
  id: string;
  source: 'own' | 'market';
  name: string;
  kind: string;
  model: string | null;
  year: number | null;
  lengthM: string | null;
  cabins: number | null;
  homeBase: string | null;
  charterName: string | null;
  addedBy: string;
  isReference: boolean;
}

export type EquipmentTier = 'baseline' | 'comfort' | 'premium';

export type EquipmentBand = 'basic' | 'typical' | 'well_equipped' | 'premium';

export type HullGroup = 'sail' | 'motor';

export interface EquipmentItem {
  id: number;
  /** MMK's stable id — the key the score weights are defined against. */
  externalId: number;
  name: string;
  /** Curated value tier, or null if MMK added the item after the weight table. */
  tier: EquipmentTier | null;
  /** What this item contributes to the score: a curated 1-10 weight. */
  points: number | null;
}

/**
 * Index where 100 = the median boat of the same hull type. Unbounded above.
 * See docs/specs/2026-09-01-equipment-score-design.md.
 */
export interface EquipmentScore {
  score: number;
  /** Raw equipment points earned. */
  points: number;
  /** Points a typical boat of this hull group carries — the index anchor. */
  medianPoints: number;
  band: EquipmentBand;
  itemsCounted: number;
  itemsApplicable: number;
  hullGroup: HullGroup;
}

export interface ComparisonProductPrice {
  product: string;
  listPrice: number | null;
  discountPct: number | null;
  priceAfterDiscount: number | null;
  currency: string;
}

export interface WeeklyComparisonBoat {
  id: string;
  source: 'own' | 'market';
  name: string;
  kind: string;
  model: string | null;
  year: number | null;
  lengthM: string | null;
  cabins: number | null;
  homeBase: string | null;
  charterName: string | null;
  externalId: string;
  companyExternalId: string | null;
  availabilityStatus:
    | 'reservation'
    | 'option'
    | 'option_expired'
    | 'service'
    | 'sleepaboard'
    | 'unavailable'
    | null;
  commissionPct: string | null;
  listPrice: number | null;
  discountPct: number | null;
  priceAfterDiscount: number | null;
  currency: string;
  product: string;
  additionalProduct?: ComparisonProductPrice;
  isOwnBoat: boolean;
  equipment: EquipmentItem[];
  /** Null when the boat has no equipment rows at all — missing data, not a bare boat. */
  equipmentScore: EquipmentScore | null;
}

export interface WeekRankBoat {
  boatPid: string;
  boatName: string;
  mmkRank: number | null;
  mmkTotal: number | null;
  boataroundRank: number | null;
  boataroundTotal: number | null;
}

export interface UpdateDiscountResult {
  listPrice: number;
  discountPct: number;
  priceAfterDiscount: number;
  pushedToProvider: boolean;
}

export interface FilterOptions {
  homeBases: string[];
  charterNames: string[];
  kinds: string[];
  shipyards: string[];
  equipment: { id: number; name: string }[];
  yearRange: { min: number; max: number } | null;
  lengthRange: { min: number; max: number } | null;
  cabinRange: { min: number; max: number } | null;
  berthRange: { min: number; max: number } | null;
}

export interface LocationHierarchyBase {
  // String because MMK base ids can exceed Number.MAX_SAFE_INTEGER.
  id: string;
  pid: string;
  name: string;
}

export interface LocationHierarchySailingArea {
  id: number;
  pid: string;
  name: string;
  bases: LocationHierarchyBase[];
}

export interface LocationHierarchyCountry {
  id: number;
  pid: string;
  name: string;
  sailingAreas: LocationHierarchySailingArea[];
}

export type LocationHierarchy = LocationHierarchyCountry[];

export interface LocationBaseNode {
  pid: string;
  name: string;
  city: string | null;
}

export interface LocationCountryNode {
  pid: string;
  name: string;
  short: string;
  bases: LocationBaseNode[];
}

export interface LocationWorldRegionNode {
  pid: string;
  name: string;
  countries: LocationCountryNode[];
}

export type LocationHierarchyRegions = LocationWorldRegionNode[];

export interface CalendarWeek {
  weekStart: string;
  weekEnd: string;
  ownPrice: number | null;
  compSetAvg: number | null;
  compSetMin: number | null;
  compSetMax: number | null;
  position: 'overpriced' | 'underpriced' | 'competitive' | null;
  positionPct: number | null;
  totalCompetitors: number;
  bookedCount: number;
  occupancyRate: number | null; // 0..1; null only when the comp set has no competitor boats
  ownTotal: number; // org's own (reference) boats in this comp set
  ownBookedCount: number; // own boats with a firm reservation this week
  ownOccupancyRate: number | null; // 0..1 own-fleet occupancy; null when no reference boats
  /**
   * This week's median price of all our boats in the set that are still free,
   * how many it covers, and how many priced ones it left out as booked.
   */
  ownMedian: number | null;
  ownMedianCount: number;
  ownMedianBookedCount: number;
  /** The same for the competitors (MMK, whole week, above zero, free that week). */
  compSetMedian: number | null;
  compSetMedianCount: number;
  compSetMedianBookedCount: number;
  /** ownMedian against compSetMedian, in percent. */
  medianPositionPct: number | null;
}

export interface SideBySideWeekPrice {
  weekStart: string;
  weekEnd: string;
  price: number | null;
  listPrice: number | null;
  discountPct: number | null;
  currency: string;
  occupancyStatus?:
    | 'reservation'
    | 'option'
    | 'option_expired'
    | 'service'
    | 'sleepaboard'
    | 'unavailable'
    | null;
}

export interface SideBySideBoatRow {
  id: string;
  source: 'own' | 'market';
  name: string;
  model: string | null;
  charterName: string | null;
  homeBase: string | null;
  isOwnBoat: boolean;
  equipment: EquipmentItem[];
  equipmentScore: EquipmentScore | null;
  weeks: SideBySideWeekPrice[];
}

export interface SideBySideResponse {
  weeks: Array<{ weekStart: string; weekEnd: string }>;
  boats: SideBySideBoatRow[];
}

export interface AlertRuleUserSummary {
  pid: string;
  name: string;
  email: string;
}

export interface AlertRule {
  id: string;
  scopeType: string;
  compSetId: string | null;
  compSetName: string | null;
  marketBoatId: string | null;
  marketBoatName: string | null;
  ruleType: string;
  thresholdPct: string | null;
  notifyInApp: boolean;
  notifyEmail: boolean;
  isActive: boolean;
  lastTriggeredAt: string | null;
  createdAt: string;
  createdBy: AlertRuleUserSummary;
  subscribers: AlertRuleUserSummary[];
  isSubscribed: boolean;
}

export interface PricingStrategy {
  id: string;
  name: string;
  strategyType: string;
  params: Record<string, unknown>;
  compSetId: string | null;
  boatId: string | null;
  isActive: boolean;
  priority: number;
  createdAt: string;
}

export interface PricingSuggestion {
  id: string;
  boatId: string;
  boatName: string;
  boatModel: string | null;
  dateFrom: string;
  dateTo: string;
  product: string;
  currentPrice: string;
  suggestedPrice: string;
  currency: string;
  strategyType: string;
  reasoning: string | null;
  compSetAvgPrice: string | null;
  compSetMinPrice: string | null;
  compSetMaxPrice: string | null;
  confidence: number | null;
  signals: string[] | null;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  reviewedAt: string | null;
  expiresAt: string;
  createdAt: string;
}

// ── Market Data API ──

export const marketDataApi = {
  browse: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    kind?: string;
    homeBase?: string;
    charterName?: string;
    shipyard?: string;
    minLength?: number;
    maxLength?: number;
    minCabins?: number;
    maxCabins?: number;
    minBerths?: number;
    maxBerths?: number;
    minYear?: number;
    maxYear?: number;
    minWc?: number;
    minPeople?: number;
    minBeam?: number;
    maxBeam?: number;
    minDraught?: number;
    maxDraught?: number;
    equipmentIds?: string;
    models?: string;
    basePids?: string;
    countryPid?: string;
    sailingAreaPids?: string;
    sortBy?: string;
    sortOrder?: string;
  }) =>
    api
      .get('market-data/browse', {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedResponse<MarketBoat>>>()
      .then((res) => res.data),

  tracked: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    kind?: string;
    homeBase?: string;
    charterName?: string;
    shipyard?: string;
    minLength?: number;
    maxLength?: number;
    minCabins?: number;
    maxCabins?: number;
    minBerths?: number;
    maxBerths?: number;
    minYear?: number;
    maxYear?: number;
    minWc?: number;
    minPeople?: number;
    minBeam?: number;
    maxBeam?: number;
    minDraught?: number;
    maxDraught?: number;
    equipmentIds?: string;
    models?: string;
    countryPid?: string;
    sailingAreaPids?: string;
    sortBy?: string;
    sortOrder?: string;
  }) =>
    api
      .get('market-data/tracked', {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedResponse<MarketBoat>>>()
      .then((res) => res.data),

  track: (externalIds: string[]) =>
    api
      .post('market-data/track', { json: { externalIds } })
      .json<ApiResponse<MarketBoat[]>>()
      .then((res) => res.data),

  untrack: (externalId: string) =>
    api
      .delete(`market-data/track/${externalId}`)
      .json<ApiResponse<{ message: string }>>()
      .then((res) => res.data),

  stats: () =>
    api
      .get('market-data/stats')
      .json<ApiResponse<MarketStats>>()
      .then((res) => res.data),

  get: (pid: string) =>
    api
      .get(`market-data/boats/${pid}`)
      .json<ApiResponse<MarketBoat>>()
      .then((res) => res.data),

  pricing: (
    pid: string,
    params?: { product?: string; currency?: string; page?: number; limit?: number }
  ) =>
    api
      .get(`market-data/boats/${pid}/pricing`, {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedResponse<MarketPricingPeriod>>>()
      .then((res) => res.data),

  priceHistory: (
    pid: string,
    params?: { product?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number }
  ) =>
    api
      .get(`market-data/boats/${pid}/price-history`, {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedResponse<MarketPriceSnapshot>>>()
      .then((res) => res.data),

  images: (pid: string) =>
    api
      .get(`market-data/boats/${pid}/images`)
      .json<
        ApiResponse<
          {
            id: number;
            url: string;
            name: string;
            description: string | null;
            sortOrder: number;
          }[]
        >
      >()
      .then((res) => res.data),

  sync: () =>
    api
      .post('market-data/sync')
      .json<ApiResponse<{ message: string }>>()
      .then((res) => res.data),

  syncBoats: () =>
    api
      .post('market-data/sync-boats')
      .json<ApiResponse<{ message: string }>>()
      .then((res) => res.data),

  syncLocations: () =>
    api
      .post('market-data/sync-locations')
      .json<ApiResponse<{ message: string }>>()
      .then((res) => res.data),

  models: (kind: string) =>
    api
      .get('market-data/models', { searchParams: { kind } })
      .json<ApiResponse<{ name: string; count: number }[]>>()
      .then((res) => res.data),

  filterOptions: () =>
    api
      .get('market-data/filter-options')
      .json<ApiResponse<FilterOptions>>()
      .then((res) => res.data),

  locationHierarchy: () =>
    api
      .get('market-data/location-hierarchy')
      .json<ApiResponse<LocationHierarchy>>()
      .then((res) => res.data),

  locationHierarchyRegions: () =>
    api
      .get('market-data/location-hierarchy-regions')
      .json<ApiResponse<LocationHierarchyRegions>>()
      .then((res) => res.data),

  charterCompanies: (filters?: {
    sailingAreaPids?: string[];
    countryPids?: string[];
    basePids?: string[];
  }) => {
    const searchParams: Record<string, string> = {};
    if (filters?.sailingAreaPids?.length)
      searchParams['sailingAreaPids'] = filters.sailingAreaPids.join(',');
    if (filters?.countryPids?.length) searchParams['countryPids'] = filters.countryPids.join(',');
    if (filters?.basePids?.length) searchParams['basePids'] = filters.basePids.join(',');
    return api
      .get('market-data/charter-companies', {
        searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
      })
      .json<ApiResponse<string[]>>()
      .then((res) => res.data);
  },
};

// ── Comp Sets API ──

export const compSetsApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) =>
    api
      .get('comp-sets', {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedResponse<CompSet>>>()
      .then((res) => res.data),

  marketStatus: () =>
    api
      .get('comp-sets/market-status')
      .json<ApiResponse<CompSetsMarketStatusResponse>>()
      .then((res) => res.data),

  get: (pid: string) =>
    api
      .get(`comp-sets/${pid}`)
      .json<ApiResponse<CompSet>>()
      .then((res) => res.data),

  create: (data: {
    name: string;
    referenceBoatPids?: string[];
    description?: string;
    filterKind?: string;
    filterSearch?: string;
    filterMinLength?: number;
    filterMaxLength?: number;
    filterHomeBases?: string[];
    filterCharterNames?: string[];
    filterModels?: string[];
    filterEquipmentIds?: number[];
    filterMinCabins?: number;
    filterMaxCabins?: number;
    filterMinYear?: number;
    filterMaxYear?: number;
    filterMinBerths?: number;
    filterMaxBerths?: number;
    filterMinWc?: number;
    filterMinPeople?: number;
    filterMinBeam?: number;
    filterMaxBeam?: number;
    filterMinDraught?: number;
    filterMaxDraught?: number;
    filterWorldRegionPids?: string[];
    filterCountryPids?: string[];
    filterSailingAreaPids?: string[];
    filterBasePids?: string[];
    excludedMarketBoatPids?: string[];
    smartSyncEnabled?: boolean;
  }) =>
    api
      .post('comp-sets', { json: data })
      .json<ApiResponse<CompSet>>()
      .then((res) => res.data),

  update: (pid: string, data: Record<string, unknown>) =>
    api
      .patch(`comp-sets/${pid}`, { json: data })
      .json<ApiResponse<CompSet>>()
      .then((res) => res.data),

  remove: (pid: string) => api.delete(`comp-sets/${pid}`),

  addBoat: (pid: string, body: { boatPid?: string; marketBoatPid?: string }) =>
    api
      .post(`comp-sets/${pid}/boats`, { json: body })
      .json<ApiResponse<{ message: string }>>()
      .then((res) => res.data),

  addBoatsBulk: (pid: string, body: { marketBoatPids: string[] }) =>
    api
      .post(`comp-sets/${pid}/boats/bulk`, { json: body })
      .json<ApiResponse<{ added: number }>>()
      .then((res) => res.data),

  listBoats: (pid: string) =>
    api
      .get(`comp-sets/${pid}/boats`)
      .json<ApiResponse<CompSetBoat[]>>()
      .then((res) => res.data),

  removeBoat: (pid: string, boatPid: string) => api.delete(`comp-sets/${pid}/boats/${boatPid}`),

  calendar: (pid: string, params?: { year?: number; product?: string; boatPid?: string }) =>
    api
      .get(`comp-sets/${pid}/calendar`, {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<CalendarWeek[]>>()
      .then((res) => res.data),

  sideBySide: (pid: string, params?: { year?: number; product?: string }) =>
    api
      .get(`comp-sets/${pid}/side-by-side`, {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<SideBySideResponse>>()
      .then((res) => res.data),

  weeklyComparison: (
    pid: string,
    params: { weekStart: string; product?: string; source?: 'mmk' | 'boataround' }
  ) =>
    api
      .get(`comp-sets/${pid}/weekly-comparison`, {
        searchParams: cleanParams(params),
      })
      .json<ApiResponse<WeeklyComparisonBoat[]>>()
      .then((res) => res.data),

  weekRank: (pid: string, params: { weekStart: string; product?: string }) =>
    api
      .get(`comp-sets/${pid}/week-rank`, {
        searchParams: cleanParams(params),
      })
      .json<ApiResponse<WeekRankBoat[]>>()
      .then((res) => res.data),

  updateDiscount: (
    pid: string,
    boatPid: string,
    data: { discountPct: number; weekStart: string; product?: string; pushToProvider?: boolean }
  ) =>
    api
      .patch(`comp-sets/${pid}/boats/${boatPid}/discount`, { json: data })
      .json<ApiResponse<UpdateDiscountResult>>()
      .then((res) => res.data),
};

// ── Alerts API ──

export const alertsApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    scopeType?: string;
    ruleType?: string;
    isActive?: string;
  }) =>
    api
      .get('alerts/rules', {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedResponse<AlertRule>>>()
      .then((res) => res.data),

  get: (pid: string) =>
    api
      .get(`alerts/rules/${pid}`)
      .json<ApiResponse<AlertRule>>()
      .then((res) => res.data),

  create: (data: {
    scopeType: string;
    ruleType: string;
    compSetPid?: string;
    boatPid?: string;
    marketBoatPid?: string;
    thresholdPct?: number;
    notifyInApp?: boolean;
    notifyEmail?: boolean;
  }) =>
    api
      .post('alerts/rules', { json: data })
      .json<ApiResponse<AlertRule>>()
      .then((res) => res.data),

  update: (
    pid: string,
    data: {
      scopeType?: string;
      compSetPid?: string;
      marketBoatPid?: string;
      ruleType?: string;
      thresholdPct?: number;
      notifyInApp?: boolean;
      notifyEmail?: boolean;
    }
  ) =>
    api
      .patch(`alerts/rules/${pid}`, { json: data })
      .json<ApiResponse<AlertRule>>()
      .then((res) => res.data),

  remove: (pid: string) => api.delete(`alerts/rules/${pid}`),

  subscribe: (pid: string) =>
    api
      .post(`alerts/rules/${pid}/subscribe`)
      .json<ApiResponse<AlertRule>>()
      .then((res) => res.data),

  unsubscribe: (pid: string) =>
    api
      .delete(`alerts/rules/${pid}/subscribe`)
      .json<ApiResponse<AlertRule>>()
      .then((res) => res.data),
};

// ── Pricing Suggestions API ──

export const pricingSuggestionsApi = {
  listStrategies: (params?: { page?: number; limit?: number; isActive?: string }) =>
    api
      .get('pricing-suggestions/strategies', {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedResponse<PricingStrategy>>>()
      .then((res) => res.data),

  createStrategy: (data: {
    name: string;
    strategyType: string;
    params: Record<string, unknown>;
    compSetPid?: string;
    boatPid?: string;
    isActive?: boolean;
    priority?: number;
  }) =>
    api
      .post('pricing-suggestions/strategies', { json: data })
      .json<ApiResponse<PricingStrategy>>()
      .then((res) => res.data),

  updateStrategy: (pid: string, data: Record<string, unknown>) =>
    api
      .patch(`pricing-suggestions/strategies/${pid}`, { json: data })
      .json<ApiResponse<PricingStrategy>>()
      .then((res) => res.data),

  removeStrategy: (pid: string) => api.delete(`pricing-suggestions/strategies/${pid}`),

  listSuggestions: (params?: {
    page?: number;
    limit?: number;
    boatPid?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }) =>
    api
      .get('pricing-suggestions', {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedResponse<PricingSuggestion>>>()
      .then((res) => res.data),

  getSuggestion: (pid: string) =>
    api
      .get(`pricing-suggestions/${pid}`)
      .json<ApiResponse<PricingSuggestion>>()
      .then((res) => res.data),

  generate: (data?: { boatPid?: string; compSetPid?: string }) =>
    api
      .post('pricing-suggestions/generate', { json: data ?? {} })
      .json<ApiResponse<{ generated: number }>>()
      .then((res) => res.data),

  review: (
    pid: string,
    data: { status: 'accepted' | 'rejected'; adjustedPrice?: number; pushToMmk?: boolean }
  ) =>
    api
      .patch(`pricing-suggestions/${pid}/review`, { json: data })
      .json<ApiResponse<PricingSuggestion>>()
      .then((res) => res.data),

  bulkReview: (data: { suggestionPids: string[]; status: 'accepted' | 'rejected' }) =>
    api
      .post('pricing-suggestions/bulk-review', { json: data })
      .json<ApiResponse<{ reviewed: number }>>()
      .then((res) => res.data),
};

// ── Local Events API ──

export interface LocalEvent {
  id: string;
  name: string;
  location: string;
  dateFrom: string;
  dateTo: string;
  category: string;
  impactRadius: string | null;
  createdAt: string;
}

export const localEventsApi = {
  list: (params?: { page?: number; limit?: number; dateFrom?: string; dateTo?: string }) =>
    api
      .get('pricing-suggestions/events', {
        searchParams: params as Record<string, string | number>,
      })
      .json<ApiResponse<PaginatedResponse<LocalEvent>>>()
      .then((res) => res.data),

  create: (data: {
    name: string;
    location: string;
    dateFrom: string;
    dateTo: string;
    category: string;
    impactRadius?: string;
  }) =>
    api
      .post('pricing-suggestions/events', { json: data })
      .json<ApiResponse<LocalEvent>>()
      .then((res) => res.data),

  update: (pid: string, data: Record<string, unknown>) =>
    api
      .patch(`pricing-suggestions/events/${pid}`, { json: data })
      .json<ApiResponse<LocalEvent>>()
      .then((res) => res.data),

  remove: (pid: string) => api.delete(`pricing-suggestions/events/${pid}`),
};

// ── Analytics API ──

export interface RevenueKpis {
  totalRevenue: number;
  averageDailyRate: number;
  revPAD: number;
  occupancyRate: number;
  bookedDays: number;
  availableDays: number;
}

export interface BoatRevenueBreakdown {
  boatPid: string;
  boatName: string;
  model: string | null;
  totalRevenue: number;
  currency: string | null;
  bookedWeeks: number;
  bookedDays: number;
  averageDailyRate: number;
  occupancyRate: number;
  targetRevenue: number | null;
  targetWeeks: number | null;
  goalCurrency: string | null;
  expectedProgressPct: number;
}

export interface MonthlyRevenue {
  month: string;
  totalRevenue: number;
  averageDailyRate: number;
  occupancyRate: number;
  bookedDays: number;
  availableDays: number;
}

export interface BookingPaceWeek {
  weekStart: string;
  weekEnd: string;
  currentBookings: number;
  lastYearBookings: number;
  pacePct: number;
}

// ── Occupancy Analytics ──

export interface OccupancySummary {
  occupancyRate: number;
  bookedDays: number;
  availableDays: number;
  prevYearOccupancyRate: number;
  avgBookingLength: number;
}

export interface OccupancyMonth {
  month: string;
  bookedDays: number;
  optionDays: number;
  unavailableDays: number;
  availableDays: number;
  occupancyRate: number;
  prevYearOccupancyRate: number;
}

export interface FleetCurrentStatus {
  boatPid: string;
  boatName: string;
  model: string | null;
  currentStatus: string;
  nextBookingDate: string | null;
  nextBookingGuest: string | null;
}

export interface AvailabilityGap {
  boatPid: string;
  boatName: string;
  dateFrom: string;
  dateTo: string;
  gapDays: number;
}

export interface AvailabilityGapsResult {
  gaps: AvailabilityGap[];
  totalGaps: number;
}

// ── Competitor Analytics ──

export interface CompetitorPositionSummary {
  yourAvgPrice: number;
  marketAvgPrice: number;
  deltaPct: number;
  activeCompSets: number;
  periodsBelowMarket: number;
  periodsAboveMarket: number;
  periodsAtMarket: number;
}

export interface CompetitorMonth {
  month: string;
  yourAvgPrice: number;
  marketAvgPrice: number;
  marketMinPrice: number;
  marketMaxPrice: number;
}

export interface CompSetAnalytics {
  compSetPid: string;
  compSetName: string;
  boatName: string;
  yourAvgPrice: number;
  marketAvgPrice: number;
  marketMinPrice: number;
  marketMaxPrice: number;
  deltaPct: number;
}

export const analyticsApi = {
  /** Years this organization has bookings or prices for — drives the header year picker. */
  availableYears: () =>
    api
      .get('analytics/years')
      .json<ApiResponse<number[]>>()
      .then((res) => res.data),

  revenue: (params?: { year?: string; boatPid?: string; dateFrom?: string; dateTo?: string }) =>
    api
      .get('analytics/revenue', { searchParams: cleanParams(params) })
      .json<ApiResponse<RevenueKpis>>()
      .then((res) => res.data),

  revenueByBoat: (params?: { year?: string }) =>
    api
      .get('analytics/revenue/by-boat', { searchParams: cleanParams(params) })
      .json<ApiResponse<BoatRevenueBreakdown[]>>()
      .then((res) => res.data),

  revenueByMonth: (params?: { year?: string }) =>
    api
      .get('analytics/revenue/by-month', { searchParams: cleanParams(params) })
      .json<ApiResponse<MonthlyRevenue[]>>()
      .then((res) => res.data),

  bookingPace: (params?: { year?: string; boatPid?: string }) =>
    api
      .get('analytics/booking-pace', { searchParams: cleanParams(params) })
      .json<ApiResponse<BookingPaceWeek[]>>()
      .then((res) => res.data),

  // ── Occupancy ──

  occupancySummary: (params?: { year?: string; boatPid?: string }) =>
    api
      .get('analytics/occupancy/summary', { searchParams: cleanParams(params) })
      .json<ApiResponse<OccupancySummary>>()
      .then((res) => res.data),

  occupancyByMonth: (params?: { year?: string; boatPid?: string }) =>
    api
      .get('analytics/occupancy/by-month', { searchParams: cleanParams(params) })
      .json<ApiResponse<OccupancyMonth[]>>()
      .then((res) => res.data),

  occupancySeason: (params?: {
    year?: string;
    boatPid?: string;
    /** Comma-separated boat pids; omit for the whole fleet. */
    boatPids?: string;
    /** Owner pid, or 'none' for boats with no owner assigned. */
    ownerPid?: string;
  }) =>
    api
      .get('analytics/occupancy/season', { searchParams: cleanParams(params) })
      .json<ApiResponse<OccupancySeasonSummary>>()
      .then((res) => res.data),

  currentFleetStatus: () =>
    api
      .get('analytics/occupancy/current-status')
      .json<ApiResponse<{ boats: FleetCurrentStatus[] }>>()
      .then((res) => res.data),

  availabilityGaps: (params?: { minDays?: number; daysAhead?: number }) =>
    api
      .get('analytics/occupancy/gaps', {
        searchParams: cleanParams(
          params
            ? {
                minDays: params.minDays != null ? String(params.minDays) : undefined,
                daysAhead: params.daysAhead != null ? String(params.daysAhead) : undefined,
              }
            : undefined
        ),
      })
      .json<ApiResponse<AvailabilityGapsResult>>()
      .then((res) => res.data),

  // ── Competitors ──

  competitorPosition: (params?: { year?: string; boatPid?: string; compSetPid?: string }) =>
    api
      .get('analytics/competitors/position', { searchParams: cleanParams(params) })
      .json<ApiResponse<CompetitorPositionSummary>>()
      .then((res) => res.data),

  competitorByMonth: (params?: { year?: string; boatPid?: string; compSetPid?: string }) =>
    api
      .get('analytics/competitors/by-month', { searchParams: cleanParams(params) })
      .json<ApiResponse<CompetitorMonth[]>>()
      .then((res) => res.data),

  setSeasonDates: (body: { year: number; startDate: string; endDate: string }) =>
    api
      .put('analytics/season-dates', { json: body })
      .json<ApiResponse<{ startDate: string; endDate: string; source: string }>>()
      .then((res) => res.data),

  clearSeasonDates: (year: string) =>
    api
      .delete('analytics/season-dates', { searchParams: { year } })
      .json<ApiResponse<{ startDate: string; endDate: string; source: string }>>()
      .then((res) => res.data),

  seasonOverview: (params?: { year?: string }) =>
    api
      .get('analytics/season-overview', { searchParams: cleanParams(params) })
      .json<ApiResponse<SeasonOverview>>()
      .then((res) => res.data),

  competitorCompSets: (params?: { year?: string }) =>
    api
      .get('analytics/competitors/comp-sets', { searchParams: cleanParams(params) })
      .json<ApiResponse<CompSetAnalytics[]>>()
      .then((res) => res.data),
};

export const incomeApi = {
  get: (params?: { year?: string; boatPid?: string }) =>
    api
      .get('analytics/income', { searchParams: cleanParams(params) })
      .json<ApiResponse<IncomeSummary>>()
      .then((res) => res.data),

  years: () =>
    api
      .get('analytics/income/years')
      .json<ApiResponse<number[]>>()
      .then((res) => res.data),

  setTaxRate: (taxRatePct: number) =>
    api
      .put('analytics/income/tax-rate', { json: { taxRatePct } })
      .json<ApiResponse<{ taxRatePct: number }>>()
      .then((res) => res.data),

  updateOwnerShare: (boatPid: string, data: UpsertOwnerShareInput) =>
    api
      .put(`analytics/income/boats/${boatPid}/owner-share`, { json: data })
      .json<ApiResponse<OwnerShareResponse>>()
      .then((res) => res.data),
};

export const revenueGoalsApi = {
  list: (year: number) =>
    api
      .get('revenue-goals', { searchParams: { year: String(year) } })
      .json<ApiResponse<{ goals: RevenueGoalResponse[] }>>()
      .then((res) => res.data.goals),

  upsert: (boatPid: string, data: UpsertRevenueGoalInput) =>
    api
      .put(`revenue-goals/${boatPid}`, { json: data })
      .json<ApiResponse<RevenueGoalResponse>>()
      .then((res) => res.data),

  remove: (boatPid: string, year: number) =>
    api
      .delete(`revenue-goals/${boatPid}`, { searchParams: { year: String(year) } })
      .json<ApiResponse<{ ok: true }>>()
      .then((res) => res.data),

  /** Fills `toYear`'s missing goals with `fromYear`'s targets. Never overwrites a goal. */
  copy: (fromYear: number, toYear: number) =>
    api
      .post('revenue-goals/copy', { json: { fromYear, toYear } })
      .json<ApiResponse<{ copied: number; kept: number }>>()
      .then((res) => res.data),
};

// ── Owners API ──

export const ownersApi = {
  // Org comes from the x-organization-id header; orgPid is only used for the
  // query-key in the hook, not the request.
  list: () =>
    api
      .get('owners')
      .json<ApiResponse<{ owners: OwnerListItem[] }>>()
      .then((res) => res.data.owners),

  /** Identity-only owner list for filters — no link tokens, any member. */
  options: () =>
    api
      .get('owners/options')
      .json<ApiResponse<{ owners: OwnerOption[] }>>()
      .then((res) => res.data.owners),
  create: (data: UpsertOwnerInput) =>
    api
      .post('owners', { json: data })
      .json<ApiResponse<{ pid: string; token: string | null }>>()
      .then((r) => r.data),
  update: (pid: string, data: UpsertOwnerInput) =>
    api
      .patch(`owners/${pid}`, { json: data })
      .json<ApiResponse<{ ok: true }>>()
      .then((r) => r.data),
  remove: (pid: string) =>
    api
      .delete(`owners/${pid}`)
      .json<ApiResponse<{ ok: true }>>()
      .then((r) => r.data),
  regenerate: (pid: string) =>
    api
      .post(`owners/${pid}/regenerate-link`)
      .json<ApiResponse<{ token: string }>>()
      .then((r) => r.data),
  revoke: (pid: string) =>
    api
      .post(`owners/${pid}/revoke-link`)
      .json<ApiResponse<{ ok: true }>>()
      .then((r) => r.data),
  sendEmail: (pid: string) =>
    api
      .post(`owners/${pid}/send-email`)
      .json<ApiResponse<{ ok: true }>>()
      .then((r) => r.data),
};

export const publicOwnerApi = {
  status: (token: string, year?: number) =>
    api
      .get(`public/owner-status/${token}`, { searchParams: year ? { year: String(year) } : {} })
      .json<ApiResponse<OwnerStatus>>()
      .then((res) => res.data),
};

// ── Notifications API ──

export interface NotificationItem {
  id: string;
  type: string;
  payload: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

export const notificationsApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    unreadOnly?: boolean;
    since?: string;
  }) =>
    api
      .get('notifications', {
        searchParams: cleanParams({
          page: params?.page?.toString(),
          limit: params?.limit?.toString(),
          type: params?.type,
          unreadOnly: params?.unreadOnly?.toString(),
          since: params?.since,
        }),
      })
      .json<ApiResponse<PaginatedResponse<NotificationItem>>>()
      .then((res) => res.data),

  unreadCount: () =>
    api
      .get('notifications/unread-count')
      .json<ApiResponse<{ count: number }>>()
      .then((res) => res.data),

  markRead: (pid: string) =>
    api
      .patch(`notifications/${pid}/read`)
      .json<ApiResponse<NotificationItem>>()
      .then((res) => res.data),

  markAllRead: () =>
    api
      .patch('notifications/read-all')
      .json<ApiResponse<{ updated: number }>>()
      .then((res) => res.data),
};

// ── Dashboard API ──

export const dashboardApi = {
  getLayout: () =>
    api
      .get('dashboard/layout')
      .json<ApiResponse<DashboardLayout | null>>()
      .then((res) => res.data),

  updateLayout: (layout: DashboardLayout) =>
    api
      .put('dashboard/layout', { json: layout })
      .json<ApiResponse<DashboardLayout>>()
      .then((res) => res.data),
};

// ── Super Admin API ──────────────────────────────────────────────────

export interface AdminCharter {
  pid: string;
  name: string;
  country: string | null;
  city: string | null;
  email: string | null;
  /** Distinct reachable numbers, mobile first. Empty when we hold none. */
  phones: string[];
  /** Null for an email never attempted with this charter. */
  emails: Record<CharterEmailKind, CharterEmailState | null>;
  /** On the Gold Partner list from issue #227. */
  isGoldPartner: boolean;
  /** ISO timestamp of the last call, or null if we have never phoned them. */
  calledAt: string | null;
  /** Free-text note. Null means none, which is what hides the list indicator. */
  note: string | null;
}

export interface CharterCountry {
  country: string;
  total: number;
}

/** Where one email stands with one charter. */
export interface CharterEmailState {
  /** ISO timestamp of the last attempt that went out, or null if none has. */
  sentAt: string | null;
  /** ISO timestamp of the last attempt, whatever its outcome. */
  lastAt: string;
  lastStatus: CharterEmailStatus;
  lastError: string | null;
}

export interface CharterEmailResult {
  pid: string;
  kind: CharterEmailKind;
  status: 'sent';
  email: string;
  createdAt: string;
}

export const adminApi = {
  dashboard: () =>
    api
      .get('admin/dashboard')
      .json<ApiResponse<AdminDashboardMetrics>>()
      .then((res) => res.data),

  companies: (params?: Record<string, string | undefined>) =>
    api
      .get('admin/companies', { searchParams: cleanParams(params) })
      .json<ApiResponse<PaginatedResponse<AdminCompany>>>()
      .then((res) => res.data),

  searchCompanies: (q: string) =>
    api
      .get('admin/companies/search', { searchParams: { q } })
      .json<ApiResponse<AdminCompanySearchResult[]>>()
      .then((res) => res.data),

  companyDetail: (pid: string) =>
    api
      .get(`admin/companies/${pid}`)
      .json<ApiResponse<AdminCompany>>()
      .then((res) => res.data),

  companyDeletionPreview: (pid: string) =>
    api
      .get(`admin/companies/${pid}/deletion-preview`)
      .json<ApiResponse<AdminCompanyDeletionPreview>>()
      .then((res) => res.data),

  // A large company's cascade can outlast ky's 10s default, and a retried DELETE
  // after a gateway timeout would report "not found" for a delete that succeeded.
  deleteCompany: (pid: string) =>
    api.delete(`admin/companies/${pid}`, { timeout: 120_000, retry: 0 }),

  charters: (params?: Record<string, string | undefined>) =>
    api
      .get('admin/charters', { searchParams: cleanParams(params) })
      .json<ApiResponse<PaginatedResponse<AdminCharter>>>()
      .then((res) => res.data),

  charterCountries: () =>
    api
      .get('admin/charters/countries')
      .json<ApiResponse<CharterCountry[]>>()
      .then((res) => res.data),

  sendCharterEmail: (pid: string, kind: CharterEmailKind) =>
    api
      .post(`admin/charters/${pid}/emails`, { json: { kind } })
      .json<ApiResponse<CharterEmailResult>>()
      .then((res) => res.data),

  setCharterNote: (pid: string, note: string | null) =>
    api.patch(`admin/charters/${pid}/note`, { json: { note } }).json<unknown>(),

  setCharterCalled: (pid: string, called: boolean) =>
    api.patch(`admin/charters/${pid}/called`, { json: { called } }).json<unknown>(),

  users: (params?: Record<string, string | undefined>) =>
    api
      .get('admin/users', { searchParams: cleanParams(params) })
      .json<ApiResponse<PaginatedResponse<AdminUser>>>()
      .then((res) => res.data),

  listJobs: () =>
    api
      .get('admin/jobs')
      .json<ApiResponse<AdminSyncJob[]>>()
      .then((res) => res.data),

  runJob: (id: AdminSyncJobId) =>
    api
      .post(`admin/jobs/${id}/run`)
      .json<ApiResponse<{ message: string }>>()
      .then((res) => res.data),
};

export const betaApi = {
  create: (data: BetaSignupInput) =>
    api
      .post('beta-signups', { json: data })
      .json<ApiResponse<BetaSignupResponse>>()
      .then((res) => res.data),
};

export interface PublicMarketStats {
  priceChangesLast24h: number;
  bookingsLast24h: number;
  priceChangesLast7d: number;
}

export const marketStatsApi = {
  publicStats: () =>
    api
      .get('market-stats/public')
      .json<ApiResponse<PublicMarketStats>>()
      .then((res) => res.data),
};

// ── Setup API ──

export interface CompanyOption {
  id: string;
  name: string;
  city: string | null;
  country: string | null;
  rating: { average: number; reviews: number } | null;
}

export interface SetupStatusResponse {
  setupCompletedAt: string | null;
  integration: {
    pid: string;
    status: string;
    lastSyncAt: string | null;
    errorMessage: string | null;
  } | null;
  syncState: {
    running: boolean;
    currentPhase: 'yachts' | 'availability' | 'reservations';
    progress: { completed: number; total: number };
  } | null;
}

export const setupApi = {
  validateKey: (orgPid: string, data: { provider: 'mmk'; apiKey: string }) =>
    api
      .post(`organizations/${orgPid}/setup/validate-key`, { json: data })
      .json<ApiResponse<{ companies: CompanyOption[] }>>()
      .then((res) => res.data),

  connect: (orgPid: string, data: { provider: 'mmk'; apiKey: string; companyExternalId: string }) =>
    api
      .post(`organizations/${orgPid}/setup/connect`, { json: data })
      .json<ApiResponse<{ integrationPid: string; syncJobId: string }>>()
      .then((res) => res.data),

  getStatus: (orgPid: string) =>
    api
      .get(`organizations/${orgPid}/setup/status`)
      .json<ApiResponse<SetupStatusResponse>>()
      .then((res) => res.data),

  retry: (orgPid: string, data: { fromPhase: 'yachts' | 'availability' | 'reservations' }) =>
    api
      .post(`organizations/${orgPid}/setup/retry`, { json: data })
      .json<ApiResponse<{ syncJobId: string }>>()
      .then((res) => res.data),
};

export { api };
